import {isValidationFailedResponse, ValidationFailures} from '@/api';
import {Script, Scripts, StrategyKind} from '@/api/models/Scripts.ts';
import ConfirmNavigationDialog from '@/components/ConfirmNavigationDialog.tsx';
import FormControls from '@/components/FormControls.tsx';
import Pending from '@/components/Pending.tsx';
import FeedSelect, {FeedSelectFn, FeedStub} from '@/components/Selects/FeedSelect.tsx';
import Title from '@/components/Title.tsx';
import ValidatedFormGroup from '@/components/ValidatedFormGroup.tsx';
import StrategySelect from '@/pages/scripts/Editor/StrategySelect.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, InputGroup} from '@blueprintjs/core';
import {ChangeEventHandler, EventHandler, ReactElement, SyntheticEvent, useCallback, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router';

export default function ScriptEditorPage(): ReactElement {
	const {id = 'new'} = useParams<'id'>();

	const script = useMemo(() => {
		if (id === 'new')
			return Promise.resolve(null);

		return Scripts.read(parseInt(id));
	}, [id]);

	return (
		<>
			<Title title={`${id === 'new' ? 'Create' : 'Edit'} Script`} />

			<Pending resource={script}>
				{script => <Content script={script} />}
			</Pending>
		</>
	);
}

interface ContentProps {
	script: Script | null,
}

function Content({script}: ContentProps): ReactElement {
	const navigate = useNavigate();

	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [validationFailures, setValidationFailures] = useState<ValidationFailures | null>(null);

	const [feed, setFeed] = useState<FeedStub | null>(script?.feed ?? null);

	const onFeedSelect: FeedSelectFn = useCallback(feed => {
		setFeed(feed);
		setDirty(true);
	}, []);

	const [selector, setSelector] = useState(script?.vinExtractionStrategy.selector ?? '');
	const onSelectorChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setSelector(event.currentTarget.value);
	}, []);

	const onSubmit: EventHandler<SyntheticEvent> = useCallback(async event => {
		event.preventDefault();

		if (!feed) {
			Toaster.warning('You must select a feed.');
			return;
		} else if (saving)
			return;

		try {
			document.querySelector(selector);
		} catch {
			Toaster.warning('The CSS selector provided is not valid.');
			return;
		}

		setSaving(true);

		let promise: Promise<Script>;
		const data = {
			feed: feed.id,
			vinExtractionStrategy: {
				kind: StrategyKind.CssSelector,
				selector,
			},
		};

		if (script)
			promise = Scripts.update(script.id, data);
		else
			promise = Scripts.create(data);

		try {
			await promise;
		} catch (error) {
			if (isValidationFailedResponse(error)) {
				Toaster.error(error.error.message);
				setValidationFailures(error.error.context.failures);
			} else
				ToastErrors.fromError(error);

			return;
		} finally {
			setSaving(false);
		}

		Toaster.success(`Your script has been ${script ? 'saved' : 'created'}.`);

		setDirty(false);
		navigate(-1);
	}, [script, feed, selector, saving]);

	const onCancelClick = useCallback(() => {
		navigate(-1);
	}, []);

	return (
		<>
			<form onSubmit={onSubmit}>
				<ValidatedFormGroup label="Feed" style={{width: '50%'}} labelFor="feed" failures={validationFailures}>
					<FeedSelect onSelect={onFeedSelect} current={feed} />
				</ValidatedFormGroup>

				<div style={{display: 'flex', gap: 10}}>
					<ValidatedFormGroup
						label="VIN Extraction Method"
						fill={true}
						style={{maxWidth: '50%'}}
						labelFor="vinExtractionStrategy.kind"
						failures={validationFailures}
					>
						<StrategySelect selected={StrategyKind.CssSelector} disabled={true} />
					</ValidatedFormGroup>

					<ValidatedFormGroup
						label="Selector"
						fill={true}
						labelFor="vinExtractionStrategy.selector"
						failures={validationFailures}
					>
						<InputGroup onChange={onSelectorChange} value={selector} />
					</ValidatedFormGroup>
				</div>

				<FormControls>
					<Button text="Cancel" onClick={onCancelClick} disabled={saving} />
					<Button type="submit" text="Save" intent="primary" onClick={onSubmit} loading={saving} />
				</FormControls>
			</form>

			<ConfirmNavigationDialog dirty={dirty} />
		</>
	);
}
